import type { SupabaseClient } from '@supabase/supabase-js';
import type { SectorYearValue } from './types';

export const OFFROAD_METHOD_VERSION = 'offroad-direct-sum-v1';
export const OFFROAD_SOURCE_TABLE = 'offroad_emissions';
export const OFFROAD_EMISSIONS_COLUMN = 'ghg_emissions_tco2e';

const INVENTORY_YEARS = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);

/** Map population geography_type to offroad_emissions geography_type. */
export function resolveOffroadGeographyType(
  populationGeographyType: string | null | undefined,
  geofips: number | string
): 'place' | 'county' | 'state' {
  if (populationGeographyType === 'City') return 'place';
  if (populationGeographyType === 'County') return 'county';
  if (populationGeographyType === 'State') return 'state';
  if (populationGeographyType) {
    const lower = String(populationGeographyType).toLowerCase();
    if (lower === 'place' || lower === 'county' || lower === 'state') return lower;
  }
  const geofipsStr = String(geofips);
  if (geofipsStr.length >= 6) return 'place';
  if (geofipsStr.length <= 2) return 'state';
  return 'county';
}

export async function fetchOffroadEmissionsByYearMt(
  supabase: SupabaseClient,
  geofips: number,
  geographyType?: string | null
): Promise<Map<number, number>> {
  const geofipsStr = String(geofips);
  let offroadGeographyType = geographyType
    ? resolveOffroadGeographyType(geographyType, geofips)
    : null;

  if (!offroadGeographyType) {
    try {
      const { data: popRow } = await supabase
        .from('population')
        .select('geography_type')
        .eq('geofips', geofips)
        .eq('year', 2023)
        .limit(1);
      offroadGeographyType = resolveOffroadGeographyType(popRow?.[0]?.geography_type, geofips);
    } catch {
      offroadGeographyType = resolveOffroadGeographyType(null, geofips);
    }
  }

  const { data: offroadData } = await supabase
    .from(OFFROAD_SOURCE_TABLE)
    .select(`year, ${OFFROAD_EMISSIONS_COLUMN}`)
    .eq('geofips', geofipsStr)
    .eq('geography_type', offroadGeographyType)
    .gte('year', 2005)
    .lte('year', 2023)
    .order('year', { ascending: true });

  const byYear = new Map<number, number>();
  for (const row of offroadData || []) {
    const year = Number(row.year);
    const emissions = Number((row as Record<string, unknown>)[OFFROAD_EMISSIONS_COLUMN] || 0);
    byYear.set(year, (byYear.get(year) || 0) + emissions);
  }
  return byYear;
}

export function offroadChartSeries(byYear: Map<number, number>): { year: number; category: string; emissions: number }[] {
  return INVENTORY_YEARS.map((year) => ({
    year,
    category: 'Offroad',
    emissions: Math.round(byYear.get(year) || 0),
  }));
}

export async function fetchOffroadSectorValues(
  supabase: SupabaseClient,
  geofips: number,
  geographyType?: string | null
): Promise<{ byYear: Map<number, SectorYearValue>; chartSeries: ReturnType<typeof offroadChartSeries> }> {
  const raw = await fetchOffroadEmissionsByYearMt(supabase, geofips, geographyType);
  const byYear = new Map<number, SectorYearValue>();
  for (const year of INVENTORY_YEARS) {
    const mt = raw.get(year) ?? 0;
    byYear.set(year, {
      year,
      sectorId: 'offroad',
      displayCategory: 'Offroad',
      emissionsMt: Math.round(mt),
      status: mt > 0 ? 'calculated' : 'calculated',
      methodVersion: OFFROAD_METHOD_VERSION,
      source: OFFROAD_SOURCE_TABLE,
    });
  }
  return { byYear, chartSeries: offroadChartSeries(raw) };
}
